import axios from "axios"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useCustomerData } from "context/customer-provider"
import type { Notification } from "types/thread"

// Every caller used to poll on its own; the top bar's bell polled both this list
// and the unread count, so each 30 s brought one list and two count requests.
// `poll: false` for lists that are refreshed another way (the bell follows the
// count — see NotificationBell).
export const useNotifications = (options?: { limit?: number; unreadOnly?: boolean }, { poll = true }: { poll?: boolean } = {}) => {
    const { customerId } = useCustomerData()
    return useQuery<Notification[]>({
        queryKey: ["notifications", customerId, options],
        queryFn: async () => {
            const params = new URLSearchParams()
            if (options?.limit) params.set("limit", String(options.limit))
            if (options?.unreadOnly) params.set("unreadOnly", "true")
            const response = await axios.get(`notifications?${params.toString()}`)
            return Array.isArray(response.data) ? response.data : []
        },
        enabled: !!customerId,
        refetchInterval: poll ? 30000 : false,
    })
}

// The bell's badge: the one background poll for notifications.
export const useUnreadCount = () => {
    const { customerId } = useCustomerData()
    return useQuery<number>({
        queryKey: ["notification-unread-count", customerId],
        queryFn: async () => {
            const response = await axios.get("notifications/unread-count")
            return typeof response.data?.count === "number" ? response.data.count : 0
        },
        enabled: !!customerId,
        refetchInterval: 30000,
    })
}

export const useMarkNotificationRead = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (notificationId: number) => {
            await axios.post(`notifications/${notificationId}/read`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["notifications"] })
            queryClient.invalidateQueries({ queryKey: ["notification-unread-count"] })
        },
    })
}

export const useMarkAllRead = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async () => {
            await axios.post("notifications/read-all")
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["notifications"] })
            queryClient.invalidateQueries({ queryKey: ["notification-unread-count"] })
        },
    })
}
